import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSelect, useSwitch } from '@chilecompra/react-kit'
import {
  Autocomplete,
  Button,
  Card,
  DataTable,
  Select,
  Switch,
  Typography,
  useAutocomplete
} from '@chilecompra/react-kit/components'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import InfoAgilePurchaseTradedProducts from '../../components/Infographics/InfoAgilePurchaseTradedProducts'
import TreemapChart from '../../components/Charts/TreemapChart.v2'
import {
  onGetAgilePurchaseTradedProductsThunk,
  onGetAgilePurchaseTradedSearchProductsThunk
} from './AgilePurchaseTradedProducts.actions'
import { onGetAgilePurchaseTradedProductsInstitutionsThunk } from './AgilePurchaseTradedProductsInstitutions.actions'
import {
  CardCollapse,
  CardCollapseIcon,
  CardTitle,
  CategoryIcon,
  Content,
  Description,
  FamilyIcon,
  GridContainer,
  GridItem,
  InfoLabel,
  Link,
  ProductIcon,
  Root,
  SectionDivider,
  SegmentIcon,
  Title
} from './AgilePurchaseTradedProducts.styles'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { getMonth } from '../../modules/utils/months'
import { NEXT_TYPES_DICTIONARY_VALUES } from '../../config/settings/constants'

const TYPE_ICONS_DICTIONARY = {
  categories: <CategoryIcon />,
  families: <FamilyIcon />,
  products: <ProductIcon />,
  segments: <SegmentIcon />
}

/**
 * The AgilePurchaseTradedProduct's container.
 */
const AgilePurchaseTradedProducts = () => {
  const dispatch = useDispatch()
  const { endYear, loading, latestUpdate, regions: regionOptions } = useSelector(state => state.topNavbar)
  const { data, options } = useSelector(state => state.agilePurchaseTradedProducts)
  const { institutions, csvInstitutions } = useSelector(state => state.agilePurchaseTradedProductsInstitutions)
  const [levels, setLevels] = useState([])
  const [level, setLevel] = useState(1)
  const [isLevelFour, setIsLevelFour] = useState(false)
  const [headingFatherId, setHeadingFatherId] = useState(0)
  const [openCollapse, setOpenCollapse] = useState(false)
  const handleOpenCollapse = useCallback(() => setOpenCollapse(currentValue => !currentValue))
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, 2020), [endYear])

  const { option, value, onSelect: handleSelect, onChange: handleChange } = useAutocomplete({})

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: getMonth() === 0 ? endYear - 1 : endYear,
    changeCallback: updatedValue => {
      resetLevels()
      dispatch(
        onGetAgilePurchaseTradedProductsThunk({
          year: updatedValue,
          region: regionValues,
          headingFatherId: 0,
          level: 1
        })
      )
      dispatch(
        onGetAgilePurchaseTradedProductsInstitutionsThunk({
          level: 1,
          heading: 0,
          region: regionValues,
          year: updatedValue
        })
      )
    },
    overwriteCallback: () => endYear
  })

  const { value: regionValues, onChange: handleChangeRegion } = useSelect({
    initialValue: 30,
    changeCallback: updatedRegions => {
      resetLevels()
      dispatch(
        onGetAgilePurchaseTradedProductsThunk({
          year: yearValue,
          region: updatedRegions,
          headingFatherId: 0,
          level: 1
        })
      )
      dispatch(
        onGetAgilePurchaseTradedProductsInstitutionsThunk({
          level: 1,
          heading: 0,
          region: updatedRegions,
          year: yearValue
        })
      )
    }
  })

  const handleChartLabelFormatter = useCallback(
    function () {
      return `
        <div style="position: relative; white-space: initial; width: 100%;text-align: left; padding: 8px; ">
          <div style="
              display: -webkit-box;
              -webkit-line-clamp: 4;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 180px;
              white-space: normal;
            ">
                    ${this.point.label}
                  </div>
          
         <strong style="display: inline-block; white-space: nowrap;">
          ${currencyValue ? `USD $${formatAmount(this.point.amountUSD)}` : `$${formatAmount(this.point.amount)}`}
          </strong>
        </div>
      `
    },
    [currencyValue]
  )

  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return []

    return data.map((item, index) => {
      const value = currencyValue ? item.amountUSD : item.amount
      return {
        id: String(item.rubroId),
        name: item.name,
        label: item.name,
        parent: item.headingFatherId !== 0 ? String(item.headingFatherId) : undefined,
        amount: item.amount,
        amountUSD: item.amountUSD,
        value,
        index,
        type: NEXT_TYPES_DICTIONARY_VALUES[item.level]?.name || 'segments'
      }
    })
  }, [yearValue, currencyValue, data])

  const handleSearchClick = useCallback(() => {
    const searchText = value?.trim().toLowerCase()
    if (!searchText) return

    let selectedOption = options.find(item => item.name?.toLowerCase() === searchText)
    if (!selectedOption) return
    handleSelect(null, selectedOption)
    resetLevels()
    setHeadingFatherId(selectedOption.value)

    let nextLevel = 0
    if (selectedOption.cat === 4) {
      nextLevel = selectedOption.cat
      selectedOption = options.find(item => item.cat === nextLevel - 1 && item.value === selectedOption.headingFather)
    } else {
      nextLevel = selectedOption.cat + 1
    }
    const rubros = buildHierarchy(selectedOption, options)
    const levelsMapList = buildLevelsMap(rubros, NEXT_TYPES_DICTIONARY_VALUES, TYPE_ICONS_DICTIONARY)

    setIsLevelFour(nextLevel === 4)
    setLevel(nextLevel)
    setLevels(levelsMapList)

    dispatch(
      onGetAgilePurchaseTradedProductsThunk({
        year: yearValue,
        region: regionValues,
        headingFatherId: selectedOption.value,
        level: nextLevel,
        callback: () => {
          resetLevels()
        }
      })
    )
  }, [value, options, handleSelect, yearValue, regionValues])

  const buildHierarchy = (selectedOption, options) => {
    const headings = []
    let current = options.find(
      opt => opt.value === selectedOption.value && opt.headingFather === selectedOption.headingFather
    )

    while (current && current.headingFather !== 0) {
      headings.unshift(current)
      current = options.find(opt => opt.value === current.headingFather && opt.cat === current.cat - 1)
    }

    if (current) {
      headings.unshift(current)
    }

    return headings
  }

  const buildLevelsMap = (headings, NEXT_TYPES_DICTIONARY_VALUES, TYPE_ICONS_DICTIONARY) => {
    return headings.map(heading => {
      const nextLevel = heading.cat + 1
      const levelInfo = NEXT_TYPES_DICTIONARY_VALUES.find(item => item.value === nextLevel)
      const iconName = levelInfo?.name
      const icon = TYPE_ICONS_DICTIONARY[iconName]

      return {
        name: heading.name,
        icon,
        type: iconName,
        id: heading.value,
        level: nextLevel
      }
    })
  }

  const handleClickMap = useCallback(
    (type, label, id) => {
      setHeadingFatherId(id)
      if (isLevelFour) return
      const nextLevel = level + 1
      setLevel(nextLevel)
      setIsLevelFour(nextLevel === 4)
      const levelInfo = NEXT_TYPES_DICTIONARY_VALUES.find(item => item.value === nextLevel)
      const iconName = levelInfo?.name
      const icon = TYPE_ICONS_DICTIONARY[iconName]

      const levelsMap = {
        name: label,
        icon,
        type,
        id,
        level: nextLevel
      }
      setLevels(prev => [...prev, levelsMap])
      dispatch(
        onGetAgilePurchaseTradedProductsThunk({
          year: yearValue,
          region: regionValues,
          headingFatherId: id,
          level: nextLevel
        })
      )
    },
    [level, yearValue, regionValues]
  )

  const handleClickTree = useCallback(
    item => {
      const nextLevel = item.level
      const updatedLevels = levels.slice(0, nextLevel - 1)
      setLevels(updatedLevels)
      setLevel(nextLevel)
      setIsLevelFour(nextLevel === 4)
      setHeadingFatherId(item.id)
      dispatch(
        onGetAgilePurchaseTradedProductsThunk({
          year: yearValue,
          region: regionValues,
          headingFatherId: item.id,
          level: item.level
        })
      )
    },
    [level, yearValue, regionValues]
  )
  const resetLevels = useCallback(() => {
    setLevels([])
    setLevel(1)
    setIsLevelFour(false)
    setHeadingFatherId(0)
  }, [])

  const handleReset = useCallback(() => {
    setLevels([])
    setLevel(1)
    setHeadingFatherId(0)
    setIsLevelFour(false)
    handleSelect()
    dispatch(
      onGetAgilePurchaseTradedProductsThunk({
        year: yearValue,
        region: regionValues,
        headingFatherId: 0,
        level: 1
      })
    )
  }, [yearValue])

  useEffect(() => {
    if (!endYear || !yearValue) return
    dispatch(
      onGetAgilePurchaseTradedProductsThunk({
        year: yearValue,
        region: regionValues,
        headingFatherId,
        level: 1
      })
    )
  }, [endYear, yearValue])

  const csv = useMemo(() => {
    return [
      ['Año', 'Región', 'Categorías (Ruta)', 'Rubro', 'Monto Transado pesos chilenos', 'Monto Transado USD'],
      ...data?.map(item => [
        yearValue,
        item.region,
        levels.length > 0 ? levels.map(c => c.name).join(' / ') : 'Rubros del Estado',
        item.name,
        item.amount,
        item.amountUSD
      ])
    ]
  }, [data])

  const {
    disableCaptureImage,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, csvInstitutions, yearValue],
    captureImageCallback: () =>
      exportToImage(
        'agile_purchase_items-and-products-treemap-chart',
        `Info_Compra_Agil_Rubros_Productos_${yearValue}`
      ),
    downloadFileCallback: () => {
      exportToCsv(csv, `Info_Compra_Agil_Rubros_Productos_${yearValue}`)
      exportToCsv(csvInstitutions, `Info_Compra_Agil_Rubros_Institucion_${yearValue}`)
    }
  })
  useEffect(() => {
    if (!yearValue) return
    const actualLevel = level === 1 ? level : level - 1
    const selectedLevel = options?.find(c => c.value === Number(headingFatherId) && c.cat === actualLevel)

    dispatch(
      onGetAgilePurchaseTradedProductsInstitutionsThunk({
        year: yearValue,
        region: regionValues,
        heading: headingFatherId,
        level: selectedLevel ? selectedLevel.cat : level
      })
    )
  }, [yearValue, regionValues, headingFatherId])

  useEffect(() => {
    dispatch(onGetAgilePurchaseTradedSearchProductsThunk({}))
  }, [])

  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Rubros transados por región
        </Typography>
        <GridContainer>
          <GridItem xs={5}>
            <Title>Rubros y productos más Transados</Title>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={5}>
            <Description>
              Revisa los montos y la cantidad de órdenes de compra realizadas mediante Compra Ágil, puedes filtrar por
              año y regiones. El gráfico permite la navegación por rubros.
            </Description>
          </GridItem>
        </GridContainer>
        <DataLayout
          disableCaptureImage={disableCaptureImage}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              <GridItem md={10} xs={12}>
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem xs={12} md={3}>
                    <Select
                      label="Año"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem xs={12} md={4}>
                    <Select
                      label="Región"
                      loading={loading}
                      options={regionOptions}
                      value={regionValues}
                      onChange={handleChangeRegion}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem md={2} xs={12}>
                <Switch
                  checked={currencyValue}
                  color="primary"
                  labelLeft="CLP"
                  labelRight="USD"
                  onChange={handleChangeCurrency}
                />
              </GridItem>
            </GridContainer>
          }
          infographic={<InfoAgilePurchaseTradedProducts />}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleChangeView}
          onDownloadFile={handleDownloadFile}
        >
          <DataLayoutChild view={DataLayoutViews.CHART}>
            <Card color="blank" elevation="1" margin="0 0 32px" padding="24px">
              <GridContainer>
                <GridItem xs={12}>
                  <TreemapChart
                    data={processedData}
                    key={`chart-${regionValues}-${currencyValue}`}
                    id="agile_purchase_items-and-products-treemap-chart"
                    levels={levels}
                    labelFormatter={handleChartLabelFormatter}
                    onClickMap={handleClickMap}
                    onClickTree={handleClickTree}
                    onReset={handleReset}
                    rootIcon={<SegmentIcon />}
                    Autocomplete={
                      <GridContainer align="right" justifyContent="flex-end" spacing={1}>
                        <GridItem xs={9.8}>
                          <Autocomplete
                            option={option}
                            options={options}
                            placeholder="Ingresa el nombre del Rubro"
                            value={value}
                            onChange={handleChange}
                            onSelect={handleSelect}
                          />
                        </GridItem>
                        <GridItem xs={2} align="right">
                          <Button color="success" type="button" onClick={handleSearchClick}>
                            Buscar
                          </Button>
                        </GridItem>
                      </GridContainer>
                    }
                  />
                  <GridContainer align="right" padding="10px 0 10px" justifyContent="flex-end" spacing={1}>
                    <GridItem xs={7}>
                      <InfoLabel>El gráfico representa desde enero hasta el último mes disponible.</InfoLabel>
                    </GridItem>
                  </GridContainer>
                  <SectionDivider />
                </GridItem>

                <GridItem xs={11} padding="20px 0 10px">
                  <CardTitle>
                    Instituciones que más compraron a través de Compra Ágil durante el {yearValue} en la{' '}
                    {regionOptions?.find(c => c.id === regionValues).name}.
                  </CardTitle>
                </GridItem>
                <GridItem alignContent="center" align="right" spacing={1} xs={1}>
                  <CardCollapseIcon open={openCollapse} onClick={handleOpenCollapse} />
                </GridItem>
              </GridContainer>
              <CardCollapse unmountOnExit in={openCollapse} timeout="auto">
                <SectionDivider />
                <GridContainer padding="32px 0 24px">
                  <GridItem xs={12}>
                    <DataTable
                      headers={[
                        { label: 'Año', value: 'year' },
                        { label: 'Región', value: 'region' },
                        { label: 'Rubro', value: 'heading' },
                        { label: 'Institución', value: 'institution' },
                        { label: 'Monto transado', value: 'amount' }
                      ]}
                      headersCellsProps={[
                        { enableSort: false, width: '10%' },
                        { enableSort: true, width: '10%' },
                        { enableSort: true, width: '20%' },
                        { enableSort: true, width: '45%' },
                        { enableSort: true, width: '15%' }
                      ]}
                      loading={loading}
                      records={institutions}
                      recordsDecorators={[
                        ({ year }) => year,
                        ({ region }) => region,
                        ({ heading }) => heading,
                        ({ institution, entCode }) => (
                          <Link to={`/ficha-organismo/${entCode}`} target="_blank" rel="noopener noreferrer">
                            {institution}
                          </Link>
                        ),
                        ({ amount, amountUSD }) =>
                          currencyValue ? `USD ${formatAmount(amountUSD)}` : `$${formatAmount(amount)}`
                      ]}
                    />
                  </GridItem>
                </GridContainer>
              </CardCollapse>
            </Card>
          </DataLayoutChild>
        </DataLayout>
      </Content>
    </Root>
  )
}

export default AgilePurchaseTradedProducts
